import React, { useState, useRef, useEffect } from 'react';

interface BackgroundVideoProps {
  videos: string[];
  overlayOpacity?: number;
  className?: string;
  randomize?: boolean;
}

const BackgroundVideo: React.FC<BackgroundVideoProps> = ({ 
  videos, 
  overlayOpacity = 0.4, 
  className = "fixed inset-0 w-screen h-screen overflow-hidden",
  randomize = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentVideo, setCurrentVideo] = useState(() => {
    if (videos.length === 0) return 0;
    if (!randomize) return 0;
    return Math.floor(Math.random() * videos.length);
  });

  // Auto-play next video when current one ends
  useEffect(() => {
    if (!randomize) return;
    const video = videoRef.current;
    if (video) {
      const handleEnded = () => {
        // Pick a random next video (different from current)
        let nextVideo;
        do {
          nextVideo = Math.floor(Math.random() * videos.length);
        } while (nextVideo === currentVideo && videos.length > 1);
        setCurrentVideo(nextVideo);
      };
      
      video.addEventListener('ended', handleEnded);
      return () => video.removeEventListener('ended', handleEnded);
    }
  }, [videos.length, currentVideo, randomize]);

  if (videos.length === 0) {
    return null;
  }

  return (
    <>
      {/* Full Screen Video Background */}
      <video
        ref={videoRef}
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
        autoPlay
        muted
        playsInline
        loop
        preload="auto"
      >
        <source src={videos[currentVideo]} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay */}
      <div 
        className="absolute top-0 left-0 w-full h-full pointer-events-none z-10"
        style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})` }}
      ></div>
    </>
  );
};

export default BackgroundVideo;
